import React, { createContext, useContext, useEffect, useState } from "react";
import * as api from "../api/auth";
import { isSupabaseConfigured, supabase } from "../lib/supabaseClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("authToken"));
  const [loading, setLoading] = useState(!!token);

  const persistAuthSession = (authResponse) => {
    if (authResponse && authResponse.token) {
      localStorage.setItem("authToken", authResponse.token);
      setToken(authResponse.token);
      setUser(authResponse.user || null);
      try {
        window.dispatchEvent(
          new CustomEvent("auth:login", { detail: { token: authResponse.token } })
        );
      } catch (e) {}
      return true;
    }
    return false;
  };

  const exchangeSupabaseSessionForJwt = async (session) => {
    if (!session || !session.access_token) {
      console.error("No Supabase session or access_token available");
      return { ok: false };
    }

    try {
      console.log("Exchanging Supabase session for JWT...", {
        hasAccessToken: !!session.access_token,
        userEmail: session.user?.email,
      });

      const res = await api.googleLogin({
        accessToken: session.access_token,
        providerToken: session.provider_token,
        user: session.user,
        expiresAt: session.expires_at,
      });

      console.log("Backend exchange response:", res);

      if (!res) {
        console.error("Empty response from backend exchange");
        return { ok: false, res: { message: "Empty backend response" } };
      }

      if (!persistAuthSession(res)) {
        console.error("Failed to persist auth session. Response:", res);
        return { ok: false, res };
      }

      // Detect if this redirect was a Supabase password-recovery flow.
      // Supabase returns a hash like `#access_token=...&type=recovery` or `type=password_recovery`.
      const urlFragment = (typeof window !== "undefined" && (window.location.hash + window.location.search)) || "";
      const isPasswordRecovery = /type=.*recovery/i.test(urlFragment);

      if (isPasswordRecovery) {
        // mark password recovery so UI can route to set-password flow instead of profile completion
        localStorage.setItem("passwordRecovery", "true");
      } else {
        // Mark as first-time OAuth login (trigger profile completion)
        localStorage.setItem("firstTimeOAuth", "true");
      }

      // Clean OAuth hash/query artifacts from URL after callback processing.
      if (window.location.hash || window.location.search.includes("code=")) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      console.log("Google OAuth login successful!");
      return { ok: true, res };
    } catch (err) {
      console.error("Error during Supabase->JWT exchange:", err);
      return { ok: false, res: { message: err?.message || "Exchange failed" } };
    }
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await api.me(token);
        if (mounted && res && res.user) {
          setUser(res.user);
        }
      } catch (err) {
        console.error("Failed to load user", err);
        localStorage.removeItem("authToken");
        setToken(null);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleLogin = async (creds) => {
    const res = await api.login(creds);
    if (persistAuthSession(res)) {
      return { ok: true, res };
    }
    return { ok: false, res };
  };

  const handleRegister = async (data) => {
    const res = await api.register(data);
    if (persistAuthSession(res)) {
      return { ok: true, res };
    }
    return { ok: false, res };
  };

  const loginWithGoogle = async () => {
    if (!isSupabaseConfigured || !supabase) {
      return {
        ok: false,
        res: {
          message:
            "Supabase is not configured. Add Supabase env vars before using Google login.",
        },
      };
    }

    const redirectTo = `${window.location.origin}/login`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        queryParams: {
          prompt: "select_account",
        },
      },
    });

    if (error) {
      return { ok: false, res: { message: error.message } };
    }

    return { ok: true, redirecting: true };
  };

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      console.warn("Supabase not configured, skipping auth listener");
      return undefined;
    }

    let mounted = true;

    const syncInitialOAuthSession = async () => {
      try {
        console.log("Checking for initial OAuth session...");
        if (localStorage.getItem("authToken")) {
          console.log("Auth token already exists, skipping Supabase sync");
          return;
        }
        const {
          data: { session },
        } = await supabase.auth.getSession();
        console.log("Initial Supabase session check:", {
          hasSession: !!session,
          hasAccessToken: !!session?.access_token,
        });
        if (!mounted || !session || !session.access_token) return;
        console.log("Syncing initial OAuth session...");
        await exchangeSupabaseSessionForJwt(session);
      } catch (err) {
        console.error("Failed to sync Supabase session", err);
      }
    };

    syncInitialOAuthSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Supabase auth state changed:", {
        event,
        hasSession: !!session,
        hasAccessToken: !!session?.access_token,
        hasAuthToken: !!localStorage.getItem("authToken"),
      });

      if (!mounted) return;

      // Always exchange on SIGNED_IN (user just logged in with OAuth)
      // This ensures we get a fresh JWT even if old token exists
      if (event === "SIGNED_IN") {
        console.log("Processing SIGNED_IN event - exchanging OAuth session for JWT...");
        try {
          await exchangeSupabaseSessionForJwt(session);
        } catch (err) {
          console.error("OAuth session exchange failed", err);
        }
      } else if (
        event === "TOKEN_REFRESHED" &&
        !localStorage.getItem("authToken")
      ) {
        console.log("Processing TOKEN_REFRESHED event...");
        try {
          await exchangeSupabaseSessionForJwt(session);
        } catch (err) {
          console.error("OAuth session exchange failed", err);
        }
      }
    });

    return () => {
      console.log("Cleaning up Supabase listener");
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    if (supabase) {
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch (err) {
        console.error("Supabase sign out failed", err);
      }
    }
    localStorage.removeItem("authToken");
    setToken(null);
    setUser(null);
    try {
      window.dispatchEvent(new Event("auth:logout"));
    } catch (e) {}
  };
  const refreshMe = async () => {
    if (!token) return null;
    try {
      const res = await api.me(token);
      if (res && res.user) setUser(res.user);
      return res?.user || null;
    } catch (e) {
      console.error("refreshMe failed", e);
      return null;
    }
  };
  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login: handleLogin,
        loginWithGoogle,
        register: handleRegister,
        logout,
        setUser,
        refreshMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
